// Mock cognitive data for Rexxengine demo results
export const RECOGNIZED_INTENT = {
  label: "Comparative Research",
  confidence: 0.94,
  query: "How do transformer models achieve contextual understanding?",
  entities: ["Transformers", "Self-Attention", "Context Windows", "Embeddings"],
};

export const COGNITIVE_SUMMARY = `Rexxengine interpreted your query as a request for a mechanistic explanation of contextual understanding in transformer architectures. The engine cross-referenced 2,140 sources, isolated the causal chain from tokenization to self-attention, and ranked results by explanatory depth rather than keyword density. Key consensus: contextual understanding emerges from parallel attention heads weighting token relationships across the full sequence.`;

export const COGNITIVE_LINKS = [
  {
    id: "cl-01",
    title: "Attention Is All You Need — The Architectural Foundation",
    summary:
      "The seminal work replacing recurrence with pure self-attention. It demonstrates that context is captured by letting every token attend to every other token in parallel, weighted by learned query-key similarity. Multi-head attention allows the model to focus on different relational subspaces simultaneously — syntax in one head, semantics in another. Positional encodings inject sequence order without sacrificing parallelism, and the result is a model that understands context through weighted relationships rather than sequential memory.",
    concepts: ["Self-Attention", "Multi-Head", "Positional Encoding", "Parallelism"],
    reliability: 98,
    source: "arxiv.org / 1706.03762",
    graph: {
      nodes: [
        { id: "Q", label: "Query", x: 30, y: 40, active: true },
        { id: "K", label: "Key", x: 120, y: 25 },
        { id: "V", label: "Value", x: 120, y: 90 },
        { id: "A", label: "Attention", x: 210, y: 55, active: true },
      ],
      edges: [
        ["Q", "A"],
        ["K", "A"],
        ["V", "A"],
        ["Q", "K"],
      ],
    },
  },
  {
    id: "cl-02",
    title: "Contextual Embeddings and the Death of Static Vectors",
    summary:
      "Where earlier models assigned one fixed vector per word, transformer embeddings are dynamic — the representation of a word shifts based on its neighbours. Rexxengine's semantic layer confirms this is the core of contextual understanding: the token 'bank' resolves to finance or river only after attention integrates surrounding evidence. This dynamic re-weighting is why transformers generalise across domains without task-specific rules.",
    concepts: ["Embeddings", "Polysemy", "Context Windows", "Disambiguation"],
    reliability: 91,
    source: "aclanthology.org / contextual-reps",
    graph: {
      nodes: [
        { id: "T", label: "Token", x: 30, y: 55, active: true },
        { id: "C", label: "Context", x: 120, y: 25 },
        { id: "E", label: "Embedding", x: 210, y: 55, active: true },
        { id: "M", label: "Meaning", x: 120, y: 90 },
      ],
      edges: [
        ["T", "C"],
        ["C", "E"],
        ["E", "M"],
        ["T", "M"],
      ],
    },
  },
  {
    id: "cl-03",
    title: "Scaling Laws — Why Bigger Context Yields Deeper Reasoning",
    summary:
      "Empirical scaling research shows that as context windows and parameter counts grow, emergent reasoning capabilities appear non-linearly. Rexxengine's ranking model weights this source highly for depth: it connects raw architecture to observable understanding. Longer windows let attention span entire documents, producing coherence that short-context models cannot reach — the practical bridge between mechanism and cognition.",
    concepts: ["Scaling Laws", "Emergence", "Long Context", "Reasoning"],
    reliability: 87,
    source: "openreview.net / scaling-emergence",
    graph: {
      nodes: [
        { id: "S", label: "Scale", x: 30, y: 40, active: true },
        { id: "W", label: "Window", x: 120, y: 60 },
        { id: "R", label: "Reasoning", x: 210, y: 40, active: true },
        { id: "P", label: "Params", x: 120, y: 20 },
      ],
      edges: [
        ["S", "W"],
        ["S", "P"],
        ["W", "R"],
        ["P", "R"],
      ],
    },
  },
];

export const CONCEPT_CLUSTERS = [
  { label: "Attention Mechanisms", count: 42 },
  { label: "Embeddings", count: 31 },
  { label: "Scaling & Emergence", count: 24 },
  { label: "Positional Encoding", count: 18 },
  { label: "Interpretability", count: 12 },
];

export const HERO_SUGGESTIONS = [
  "Explain quantum error correction",
  "Compare RAG vs fine-tuning",
  "https://arxiv.org/abs/1706.03762",
  "What causes inflation cycles?",
];
